import init, { panel, type Panel } from "../vendor/web";
import { bodyDiagram, locationSheet, type LocationSurface } from "./body";
import {
  armouredLocations,
  browsePanel,
  offeredShops,
  readFacets,
  writeFacets,
} from "./browse";
import {
  add,
  quantityOf,
  remove,
  setEquipped,
  setQuality,
  setQuantity,
  setSpell,
  spliceInput,
  type CartLine,
} from "./cart";
import {
  countsByItem,
  loadBaked,
  loadCatalogue,
  loadSheet,
  shopsByItem,
  type Catalogue,
} from "./catalogue";
import { kitTab } from "./kit";
import { pinnedStrip } from "./pin";
import { stalls, type Stall } from "./quality";
import type { ShopActions } from "./shop";
import { closeSheet, openSheet, refreshSheet, sheetIsOpen } from "./sheet";
import { paged } from "./spells";
import { held, readRow, writeRow, type Held } from "./store";
import { weaponList } from "./weapons";

const TABS = ["Body", "Weapons", "Kit", "Browse"] as const;
type Tab = (typeof TABS)[number];
const DRAWN: Tab[] = ["Body", "Weapons", "Kit", "Browse"];

interface Open {
  slug: string;
  name: string;
  fingerprint: string;
  sheet: string;
  deltas: string;
  stalls: Stall[];
}

const app = document.querySelector<HTMLElement>("#app");
if (!app) throw new Error("no #app");
const root = app;

let catalogue: Catalogue;
let open: Open | null = null;
let cart: CartLine[] = [];
let unequipped: string[] = [];
let done = false;
let tab: Tab = "Body";
/** The hit location whose sheet is open, so buying from it redraws it in place. */
let opened: string | null = null;
/** The spell each page row names, and the tier each row is selected at, before either is
 *  bought. Add is the only control that buys, so both choices live here until it runs. */
let chosenSpells = new Map<string, string>();
let chosenTiers = new Map<string, string>();

async function start(): Promise<void> {
  await init({ module_or_path: "/vendor/web_bg.wasm" });
  catalogue = await loadCatalogue();
  window.addEventListener("popstate", () => {
    if (sheetIsOpen()) {
      closeSheet();
      opened = null;
      return;
    }
    void route();
  });
  await route();
}

function slugFromPath(): string | null {
  const slug = location.pathname.replace(/^\/+|\/+$/g, "");
  return slug === "" ? null : slug;
}

async function route(): Promise<void> {
  const slug = slugFromPath();
  const entry = slug === null ? undefined : catalogue.characters[slug];
  if (slug === null || !entry) {
    open = null;
    await renderPicker();
    return;
  }
  root.replaceChildren(loading(entry.name));
  const [sheet, baked, row] = await Promise.all([loadSheet(entry), loadBaked(entry), readRow(slug)]);
  const stored = held(row, entry.fingerprint);
  open = {
    slug,
    name: entry.name,
    fingerprint: entry.fingerprint,
    sheet,
    deltas: baked.deltas,
    stalls: stalls(baked.rows, catalogue.quality),
  };
  cart = stored.lines;
  unequipped = stored.unequipped;
  done = stored.done;
  tab = "Body";
  opened = null;
  chosenSpells = new Map();
  chosenTiers = new Map();
  draw();
}

function persist(): void {
  if (!open) return;
  void writeRow(open.slug, open.fingerprint, { lines: cart, unequipped, done });
}

function navigate(slug: string): void {
  history.pushState(null, "", `/${slug}`);
  void route();
}

function loading(name: string): HTMLElement {
  const element = document.createElement("p");
  element.className = "loading";
  element.textContent = `Working out ${name}...`;
  return element;
}

function marking(stored: Held): string {
  if (stored.done) return "done";
  const touched = stored.lines.length > 0 || stored.unequipped.length > 0;
  return touched ? "shopping" : "not started";
}

async function renderPicker(): Promise<void> {
  document.title = "Quartermaster";
  const page = document.createElement("main");
  page.className = "picker";

  const heading = document.createElement("h1");
  heading.textContent = "Quartermaster";
  page.append(heading);

  const party = Object.entries(catalogue.characters);
  const stored = await Promise.all(
    party.map(async ([slug, entry]) => held(await readRow(slug), entry.fingerprint)),
  );

  const list = document.createElement("ul");
  list.className = "picker-list";
  party.forEach(([slug, entry], index) => {
    const item = document.createElement("li");
    const button = document.createElement("button");
    button.className = "picker-card";
    button.dataset["slug"] = slug;
    button.dataset["done"] = String(stored[index]?.done ?? false);

    const name = document.createElement("span");
    name.className = "picker-name";
    name.textContent = entry.name;

    const marker = document.createElement("span");
    marker.className = "picker-marker";
    marker.textContent = marking(stored[index] ?? { lines: [], unequipped: [], done: false });

    button.append(name, marker);
    button.addEventListener("click", () => navigate(slug));
    item.append(button);
    list.append(item);
  });
  page.append(list);
  root.replaceChildren(page);
}

function draw(): void {
  if (!open) return;
  const view = panel(open.sheet, spliceInput(cart, unequipped, open.stalls), open.deltas);
  document.title = `${open.name} - Quartermaster`;

  const page = document.createElement("main");
  page.className = "character";
  page.append(header(view), tabs(), body(view), pinnedStrip(view));
  root.replaceChildren(page);
  root.dataset["slug"] = open.slug;

  const held = opened === null ? undefined : view.locations.find((loc) => loc.id === opened);
  if (held) refreshSheet(locationSheet(held, locationSurface(view)));
}

/** The cart levers every buying surface is handed, so a row bought from a location sheet
 *  and the same row bought from Browse write the same line. */
function shopActions(): ShopActions {
  const changed = (lines: CartLine[]): void => {
    cart = lines;
    persist();
    draw();
  };
  const counts = countsByItem(catalogue);
  const count = (item: string): number => counts.get(item) ?? 1;
  return {
    add: (item) =>
      changed(add(cart, item, count(item), chosenSpells.get(item), chosenTiers.get(item))),
    remove: (item) => changed(remove(cart, item)),
    setQuantity: (item, quantity) => changed(setQuantity(cart, item, quantity)),
    setQuality: (item, quality) => {
      if (quantityOf(cart, item) === 0) {
        if (quality === undefined) chosenTiers.delete(item);
        else chosenTiers.set(item, quality);
        draw();
        return;
      }
      changed(setQuality(cart, item, quality));
    },
    setSpell: (item, spell) => {
      chosenSpells.set(item, spell);
      changed(setSpell(cart, item, spell));
    },
  };
}

function arrivedWith(view: Panel): ReadonlySet<string> {
  return new Set(
    view.owned
      .map((row) => row.catalogue_id)
      .filter((id): id is string => id !== undefined && id !== null),
  );
}

function locationSurface(view: Panel): LocationSurface {
  return {
    stalls: open?.stalls ?? [],
    lines: cart,
    actions: shopActions(),
    table: catalogue.quality,
    owned: arrivedWith(view),
    counts: countsByItem(catalogue),
    tiers: chosenTiers,
    locations: view.locations,
  };
}

function header(view: Panel): HTMLElement {
  const bar = document.createElement("header");
  bar.className = "character-header";
  const back = document.createElement("button");
  back.className = "back";
  back.textContent = "< Party";
  back.addEventListener("click", () => {
    history.pushState(null, "", "/");
    void route();
  });
  const name = document.createElement("h1");
  name.textContent = view.name;

  const marker = document.createElement("button");
  marker.className = done ? "done done-set" : "done";
  marker.dataset["done"] = String(done);
  marker.textContent = done ? "Done" : "Mark done";
  marker.addEventListener("click", () => {
    done = !done;
    persist();
    draw();
  });

  bar.append(back, name, marker);
  return bar;
}

function tabs(): HTMLElement {
  const bar = document.createElement("nav");
  bar.className = "tabs";
  for (const label of TABS) {
    const button = document.createElement("button");
    button.className = label === tab ? "tab tab-current" : "tab";
    button.textContent = label;
    button.disabled = !DRAWN.includes(label);
    button.addEventListener("click", () => {
      tab = label;
      draw();
    });
    bar.append(button);
  }
  return bar;
}

function body(view: Panel): HTMLElement {
  if (tab === "Weapons") return weaponList(view);
  const actions = shopActions();
  if (tab === "Kit") {
    return kitTab(view, catalogue.kit ?? [], open?.stalls ?? [], cart, {
      setEquipped: (id, on) => {
        unequipped = setEquipped(unequipped, id, on);
        persist();
        draw();
      },
      add: actions.add,
    });
  }
  if (!open || tab !== "Browse") {
    return bodyDiagram(view.locations, (held) => {
      opened = held.id;
      openSheet(held.name, locationSheet(held, locationSurface(view)));
    });
  }
  const slug = open.slug;
  const declared = catalogue.shops ?? [];
  const stocked = shopsByItem(catalogue);
  const shopsOf = (item: string): string[] => stocked.get(item) ?? [];
  return browsePanel({
    stalls: open.stalls,
    lines: cart,
    actions,
    table: catalogue.quality,
    pages: {
      spells: view.spells,
      held: paged(view.owned, cart, open.stalls),
      chosen: chosenSpells,
      advisory: catalogue.spell_page_advisory ?? "",
    },
    owned: arrivedWith(view),
    counts: countsByItem(catalogue),
    tiers: chosenTiers,
    locations: view.locations,
    declared,
    shopsOf,
    facets: readFacets(
      location.search,
      offeredShops(open.stalls, declared, shopsOf),
      armouredLocations(open.stalls, view.locations).map((where) => where.id),
    ),
    onFacets: (facets) => history.replaceState(null, "", `/${slug}${writeFacets(facets)}`),
  });
}

void start();
